import { del, get, patch, post } from '../../api/api'
import { GroupMemberModel, GroupModel, GroupRole, GroupUpdateModel } from '../../models/GroupModel'
import { useAppStore } from '../../store/appStore'
import { showMessage } from '../../utils/snackbar'
import i18n from '../../i18n'

export class GroupManager {
    public async fetchGroups(): Promise<void> {
        try {
            const groups = await get<GroupModel[]>('/groups', 3)
            useAppStore.setState({ groups, groupsLoaded: true })
        } catch (e) {
            console.error('[GroupManager] Failed to fetch groups:', e)
            showMessage(i18n.t('errors.fetch_groups'), 'error')
        }
    }

    public async createGroup(name: string, description: string | null, color: string): Promise<GroupModel> {
        const group = await post<GroupModel>('/groups', { name, description, color })
        useAppStore.setState({ groups: [...useAppStore.getState().groups, group] })
        showMessage(i18n.t('groups.created', { name }), 'success')
        return group
    }

    public async updateGroup(groupId: string, changes: GroupUpdateModel): Promise<void> {
        await patch(`/groups/${groupId}`, changes)
        useAppStore.setState({
            groups: useAppStore.getState().groups.map((g) => (g.id === groupId ? { ...g, ...changes } : g)),
        })
        showMessage(i18n.t('groups.updated'), 'success')
    }

    public async deleteGroup(groupId: string): Promise<void> {
        await del(`/groups/${groupId}`)
        useAppStore.setState({ groups: useAppStore.getState().groups.filter((g) => g.id !== groupId) })
        showMessage(i18n.t('groups.deleted'), 'success')
    }

    public async fetchGroupMembers(groupId: string): Promise<GroupMemberModel[]> {
        return await get<GroupMemberModel[]>(`/groups/${groupId}/users`)
    }

    public async addGroupMember(groupId: string, userId: string): Promise<void> {
        await post(`/groups/${groupId}/users`, { user_id: userId })
        this.adjustMemberCount(groupId, 1)
        showMessage(i18n.t('groups.member_added'), 'success')
    }

    public async removeGroupMember(groupId: string, userId: string): Promise<void> {
        await del(`/groups/${groupId}/users/${userId}`)
        this.adjustMemberCount(groupId, -1)
        showMessage(i18n.t('groups.member_removed'), 'success')
    }

    private adjustMemberCount(groupId: string, delta: number): void {
        useAppStore.setState({
            groups: useAppStore
                .getState()
                .groups.map((g) => (g.id === groupId ? { ...g, member_count: g.member_count + delta } : g)),
        })
    }

    public async setMemberRole(groupId: string, userId: string, role: GroupRole): Promise<void> {
        await patch(`/groups/${groupId}/users/${userId}`, { role })
        showMessage(i18n.t('groups.member_updated'), 'success')
    }
}

export const groupManager = new GroupManager()
